import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT_EXPO } from '../../../../components/AnimatedSection/AnimatedSection';
import './JoinCarouselSection.scss';

import photo1 from '../../../../assets/images/carousel/carousel-photo1.webp';
import photo2 from '../../../../assets/images/carousel/carousel-photo2.jpg';
import photo3 from '../../../../assets/images/carousel/carousel-photo3.webp';
import photo4 from '../../../../assets/images/carousel/carousel-photo4.webp';
import photo5 from '../../../../assets/images/carousel/carousel-photo5.webp';
import photo6 from '../../../../assets/images/carousel/carousel-photo6.webp';
import photo7 from '../../../../assets/images/carousel/carousel-photo7.webp';
import photo8 from '../../../../assets/images/carousel/carousel-photo8.webp';
import photo9 from '../../../../assets/images/carousel/carousel-photo9.webp';
import photo10 from '../../../../assets/images/carousel/carousel-photo10.webp';
import photo11 from '../../../../assets/images/carousel/carousel-photo11.webp';
import photo12 from '../../../../assets/images/carousel/carousel-photo12.webp';

const JoinCarouselSection = () => {
    const { t } = useTranslation();
    const prefersReducedMotion = useReducedMotion();
    const photosPerView = 4;
    const totalPhotos = 12;

    const photos = [
        photo1, photo2, photo3, photo4,
        photo5, photo6, photo7, photo8,
        photo9, photo10, photo11, photo12
    ];

    const extendedPhotos = [...photos, ...photos, ...photos];
    const [currentIndex, setCurrentIndex] = useState(photos.length);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const carouselRef = useRef(null);
    const autoPlayRef = useRef(null);

    const fadeUp = (delay, duration = 1.0) =>
        prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, amount: 0.25 },
                  transition: { duration, delay, ease: EASE_OUT_EXPO },
              };

    const getTranslateX = () => {
        const itemWidth = 305 + 20;
        return -(currentIndex * itemWidth);
    };

    const nextSlide = useCallback(() => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentIndex(prev => prev + 1);
    }, [isAnimating]);

    const prevSlide = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        setCurrentIndex(prev => prev - 1);
    };

    useEffect(() => {
        if (isAutoPlaying) {
            autoPlayRef.current = setInterval(() => {
                nextSlide();
            }, 4000);
        }

        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current);
            }
        };
    }, [isAutoPlaying, isAnimating, nextSlide]);

    const handleMouseEnter = () => {
        setIsAutoPlaying(false);
        if (autoPlayRef.current) {
            clearInterval(autoPlayRef.current);
        }
    };

    const handleMouseLeave = () => {
        setIsAutoPlaying(true);
    };

    useEffect(() => {
        if (!isAnimating) return;

        const timeout = setTimeout(() => {
            if (currentIndex >= photos.length * 2) {
                setCurrentIndex(photos.length);
                setTimeout(() => setIsAnimating(false), 10);
            } else if (currentIndex < photos.length) {
                setCurrentIndex(photos.length * 2 - photosPerView);
                setTimeout(() => setIsAnimating(false), 10);
            } else {
                setIsAnimating(false);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [currentIndex, isAnimating, photos.length]);

    return (
        <section className="join-carousel">
            <div className="join-carousel__outer">
                <motion.div className="join-carousel__container" {...fadeUp(0)}>
                    <div
                        className="join-carousel__carousel"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <button
                            className="join-carousel__arrow join-carousel__arrow--left"
                            onClick={prevSlide}
                            disabled={isAnimating}
                            aria-label={t('aria_previous_photos')}
                        >
                            <svg className="join-carousel__arrow-icon" viewBox="0 0 320 512">
                                <path d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"></path>
                            </svg>
                        </button>

                        <div className="join-carousel__photos-wrapper" ref={carouselRef}>
                            <div
                                className="join-carousel__photos"
                                style={{
                                    transform: `translateX(${getTranslateX()}px)`,
                                    transition: isAnimating ? 'transform 0.5s ease-in-out' : 'none'
                                }}
                            >
                                {extendedPhotos.map((photo, index) => (
                                    <div key={index} className="join-carousel__photo-item">
                                        <img src={photo} alt={t('alt_gallery', { number: index % totalPhotos + 1 })} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            className="join-carousel__arrow join-carousel__arrow--right"
                            onClick={nextSlide}
                            disabled={isAnimating}
                            aria-label={t('aria_next_photos')}
                        >
                            <svg className="join-carousel__arrow-icon" viewBox="0 0 320 512">
                                <path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path>
                            </svg>
                        </button>
                    </div>

                    <div className="join-carousel__indicators">
                        {[...Array(totalPhotos)].map((_, index) => (
                            <button
                                key={index}
                                className={`join-carousel__indicator ${
                                    (currentIndex % totalPhotos) === index ? 'active' : ''
                                }`}
                                onClick={() => {
                                    if (isAnimating) return;
                                    setIsAutoPlaying(false);
                                    if (autoPlayRef.current) {
                                        clearInterval(autoPlayRef.current);
                                    }
                                    setIsAnimating(true);
                                    setCurrentIndex(photos.length + index);
                                    setTimeout(() => setIsAutoPlaying(true), 3000);
                                }}
                            />
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default JoinCarouselSection;